import { Link } from 'react-router';
import { Club } from '@repo/types';
import { ClubNameType } from '../../../fixtures/contracts';
import { Image, ImageVariant } from '../../../common/components/Image';
import { defaultTeamBadge } from '@ui/common/staticAssets.constants';
import { resolveClubName } from './resolveClubName';

export type ClubCardProps = {
  club: Club;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  /** Fires when the card's link is followed — a surface that overlays the page (GlobalSearch, the
   * mega menu) closes itself on this. The card owns its own anchor, so a caller cannot wrap it in
   * one: nested `<a>` is invalid and the browser splits it into two broken links. */
  onNavigate?: () => void;
  /**
   * Render the badge without the name beneath it (SC-304's logo-only club list). The name is
   * still the image's `alt`, so the card keeps its accessible name and the link is still
   * announced as the club rather than as an unlabelled image.
   */
  hideName?: boolean;
};

function ClubCard({
  club: { slug, fullName, shortName, nickName, threeLetterName, logo },
  nameType = ClubNameType.FullName,
  style,
  onNavigate,
  hideName = false,
}: ClubCardProps): JSX.Element {
  const clubName = resolveClubName({ fullName, shortName, threeLetterName, nickName }, nameType);

  return (
    <div className="club-card" style={style}>
      <Link
        className="club-card__link"
        to={`/clubs/${slug}`}
        // `alt` names the link for assistive technology but produces no tooltip in any current
        // browser, so with the visible name gone a sighted pointer user would have nothing to
        // tell two default badges apart. Does not disturb the accessible name — `alt` still wins.
        title={hideName ? clubName : undefined}
        onClick={onNavigate}
      >
        <Image
          className="club-card__logo"
          alt={clubName}
          src={logo || defaultTeamBadge}
          width="150"
          height="150"
          variant={ImageVariant.SquareLogoM}
        />
        {!hideName && <h4 className="club-card__name">{clubName}</h4>}
      </Link>
    </div>
  );
}

export default ClubCard;
